import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Church, ExternalLink, AlertCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import { supabase } from '../../lib/supabase';
import { useDesign } from '@/contexts/DesignContext';
import { StudioAuthShell, StudioGateCard, StudioProcessCard } from '@/components/studio/auth/StudioAuthParts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Loom video component for onboarding
function LoomVideo({ videoId, studio = false }) {
  if (studio) {
    return (
      <div>
        <div className="st-au-video">
          <iframe
            src={`https://www.loom.com/embed/${videoId}`}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
            title="Welcome video"
            loading="lazy"
          />
        </div>
        <div style={{ textAlign: 'right', marginTop: '6px' }}>
          <a
            href={`https://www.loom.com/share/${videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="st-au-lnk"
            style={{ fontSize: '12px' }}
            data-testid="signup-welcome-video-fallback"
          >
            <ExternalLink className="w-3 h-3" />
            Trouble playing? Open in Loom
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-6">
      <div
        className="rounded-lg overflow-hidden"
        style={{
          position: 'relative',
          width: '100%',
          paddingBottom: '56.25%',
          backgroundColor: '#000'
        }}
      >
        <iframe
          src={`https://www.loom.com/embed/${videoId}`}
          frameBorder="0"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title="Welcome video"
          loading="lazy"
        />
      </div>
      <div className="mt-1.5 text-right">
        <a
          href={`https://www.loom.com/share/${videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500 hover:text-indigo-500 dark:hover:text-indigo-400"
          data-testid="signup-welcome-video-fallback"
        >
          <ExternalLink className="w-3 h-3" />
          Trouble playing? Open in Loom
        </a>
      </div>
    </div>
  );
}

function SignupSuccessPage({ onComplete, onLogin, onAutoLogin }) {
  const { isStudio } = useDesign();
  const [status, setStatus] = useState('checking'); // checking, success, delayed, error
  const [message, setMessage] = useState('Setting up your church account...');
  const [pendingEmail, setPendingEmail] = useState(null);
  const [paidConfirmed, setPaidConfirmed] = useState(false); // backend saw Stripe mark the session paid
  const [userData, setUserData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const pollAttemptsRef = useRef(0);
  const cancelledRef = useRef(false); // stops timers after unmount
  const maxAttempts = 15;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const signupId = urlParams.get('signup_id');
    
    if (!signupId) {
      queueMicrotask(() => {
        setStatus('error');
        setMessage('No signup information found');
      });
      return;
    }

    const pollSignupStatus = async () => {
      try {
        // Request session tokens for auto-login
        const response = await fetch(`${API_URL}/api/stripe/signup-status/${signupId}?include_session=true`);
        const data = await response.json();

        if (data.status === 'completed') {
          setStatus('success');
          setMessage('Your church account is ready!');
          setUserData(data);
          
          // Store session data for auto-login
          if (data.session) {
            setSessionData(data.session);
          }
          
          // Celebration confetti!
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 }
          });
          
          // Clear URL params
          window.history.replaceState({}, document.title, '/signup-success');
          
        } else if (data.status === 'not_found') {
          setStatus('error');
          setMessage("We couldn't find this sign-up. If you completed payment, email support@worshipos.app with your church name and we'll finish the setup for you.");
        } else if (data.status === 'error') {
          setStatus('error');
          setMessage("There was an issue setting up your account. We've been notified and will email you as soon as it's fixed — or reply to that email if you need us sooner.");
        } else if (pollAttemptsRef.current >= maxAttempts) {
          // R25 (DIV-033): still not created after ~30 s. Do NOT claim success.
          // Tell the truth, keep polling slowly in the background (a slow
          // webhook still auto-logs them in), and promise the email the
          // backend sweep sends when it finishes or fails.
          if (data.admin_email) setPendingEmail(data.admin_email);
          if (data.paid === true) setPaidConfirmed(true);
          setStatus('delayed');
          if (pollAttemptsRef.current < maxAttempts + 30 && !cancelledRef.current) {
            pollAttemptsRef.current += 1;
            setTimeout(pollSignupStatus, 10000);
          }
        } else {
          // Keep polling
          pollAttemptsRef.current += 1;
          setTimeout(pollSignupStatus, 2000);
        }
      } catch (err) {
        console.error('Error checking signup status:', err);
        if (cancelledRef.current) return;
        if (pollAttemptsRef.current >= maxAttempts) {
          // Delayed phase: one network blip must not flip a "still setting up"
          // page into a false error. Keep the slow poll going until the cap.
          if (pollAttemptsRef.current < maxAttempts + 30) {
            pollAttemptsRef.current += 1;
            setTimeout(pollSignupStatus, 10000);
          }
        } else if (pollAttemptsRef.current >= 5) {
          setStatus('error');
          setMessage('Error verifying signup. Please check your email for confirmation.');
        } else {
          pollAttemptsRef.current += 1;
          setTimeout(pollSignupStatus, 2000);
        }
      }
    };

    // Start polling
    pollSignupStatus();
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = async () => {
    setIsLoggingIn(true);
    
    try {
      // If we have session data, use it for auto-login
      if (sessionData && sessionData.access_token) {
        await supabase.auth.setSession({
          access_token: sessionData.access_token,
          refresh_token: sessionData.refresh_token
        });
        
        // Redirect to dashboard
        window.location.href = '/';
        return;
      }
      
      // Fallback: If onAutoLogin is provided and we have user data
      if (onAutoLogin && userData) {
        onAutoLogin(userData);
        return;
      }
      
      // Legacy fallback: redirect to login with pre-filled email
      if (userData && userData.user_email) {
        if (onLogin) {
          onLogin(userData.user_email);
        } else {
          window.location.href = '/?email=' + encodeURIComponent(userData.user_email);
        }
      } else if (onComplete) {
        onComplete();
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Auto-login error:', err);
      // Fallback to login page
      window.location.href = '/';
    } finally {
      setIsLoggingIn(false);
    }
  };

  // ---- Studio (doc 119 §24): the process card while it works, the ok gate on
  // success, the bad gate on failure. Polling, confetti and actions unchanged.
  if (isStudio) {
    return (
      <StudioAuthShell>
        {status === 'checking' && (
          <StudioProcessCard title="Setting up your account" sentence={message} />
        )}

        {status === 'delayed' && (
          <StudioProcessCard
            testid="signup-delayed-title"
            title="Still setting up your church"
            sentence={<>
              {paidConfirmed ? 'Your payment went through. ' : ''}Setup is taking longer than usual. It usually finishes within a few minutes.
              <br /><br />
              We&apos;ll email {pendingEmail ? <b>{pendingEmail}</b> : 'you'} the moment it&apos;s ready. You can close this page.
            </>}
          />
        )}

        {status === 'success' && (
          <div className="st-au-card st-au-c">
            <div className="st-au-head st-au-c">
              <span className="st-au-glyph st-au-ok"><CheckCircle2 /></span>
              <h1 className="st-au-t">Welcome to Worship OS!</h1>
            </div>
            <div className="st-au-form" style={{ gap: '12px' }}>
              <LoomVideo videoId="bcc7747fdc844a09ab7d8093b2c96ba0" studio />
              <div className="st-au-chk">
                <b>Your 14-day trial includes</b>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li><CheckCircle2 />Full access to all features</li>
                  <li><CheckCircle2 />Team communication tools</li>
                  <li><CheckCircle2 />AI-powered worship assistant</li>
                  <li><CheckCircle2 />Onboarding &amp; training tools</li>
                </ul>
              </div>
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={handleContinue}
                disabled={isLoggingIn}
                className={`st-au-btn st-au-pri st-au-lg st-au-full${isLoggingIn ? ' st-au-busy' : ''}`}
              >
                {isLoggingIn ? (
                  <><span className="st-au-spin" /><span>Taking you to your dashboard…</span></>
                ) : (
                  <><span>Go to dashboard</span><ArrowRight /></>
                )}
              </Button>
              <div className="st-au-help">Check your email for sign-in details and a welcome message</div>
            </div>
          </div>
        )}

        {status === 'error' && (
          <StudioGateCard icon={AlertCircle} kind="bad" title="Something went wrong" sentence={message}>
            <div className="st-au-form" style={{ gap: '10px' }}>
              <Button onClick={handleContinue} className="st-au-btn st-au-pri st-au-lg st-au-full">Try signing in</Button>
              <Button
                onClick={() => window.location.href = 'mailto:support@worshipos.app'}
                className="st-au-btn st-au-sec st-au-lg st-au-full"
              >
                Contact support
              </Button>
            </div>
          </StudioGateCard>
        )}
      </StudioAuthShell>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 dark:from-indigo-950/40 via-white dark:via-[#1e1f22] to-purple-50 flex items-center justify-center p-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-100 dark:bg-indigo-900/50 rounded-full blur-3xl opacity-50" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 dark:bg-purple-900/50 rounded-full blur-3xl opacity-50" />
      </div>

      <Card className="w-full max-w-lg shadow-2xl border-0 relative z-10">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-indigo-600 dark:text-indigo-400 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                Setting Up Your Account
              </h2>
              <p className="text-slate-500 dark:text-slate-400">{message}</p>
              <div className="mt-6 flex justify-center gap-1">
                {[...Array(3)].map((_, i) => (
                  <span 
                    key={i}
                    className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </div>
            </>
          )}

          {status === 'delayed' && (
            <>
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-amber-600 dark:text-amber-400 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" data-testid="signup-delayed-title">
                Still Setting Up Your Church
              </h2>
              <p className="text-slate-500 dark:text-slate-400">
                {paidConfirmed ? 'Your payment went through. ' : ''}Setup is taking longer than usual — this usually finishes within a few minutes.
              </p>
              <p className="text-slate-500 dark:text-slate-400 mt-3">
                We'll email {pendingEmail ? <span className="font-medium text-slate-700 dark:text-slate-200">{pendingEmail}</span> : 'you'} the moment it's ready. You can close this page.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                Welcome to Worship OS!
              </h2>
              
              {/* Loom onboarding video */}
              <LoomVideo videoId="bcc7747fdc844a09ab7d8093b2c96ba0" />
              
              {/* Feature highlights */}
              <div className="bg-gradient-to-r from-indigo-50 dark:from-indigo-950/40 to-purple-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                  <Church className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Your 14-Day Trial Includes:
                </h3>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Full access to all features
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Team communication tools
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    AI-powered worship assistant
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Onboarding & training tools
                  </li>
                </ul>
              </div>
              
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={handleContinue}
                disabled={isLoggingIn}
                className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Taking you to your dashboard...
                  </>
                ) : (
                  <>
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">
                Check your email for login details and a welcome message
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                Something Went Wrong
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={handleContinue}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  Try Signing In
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:support@worshipos.app'}
                  className="w-full"
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SignupSuccessPage;
